import { useEffect, useState } from "react";
import { api, asArray } from "@/lib/api";
import { SeverityBadge, StatusBadge, KPI } from "@/components/Primitives";
import { Sparkles, ShieldAlert } from "lucide-react";

export default function Security() {
  const [items, setItems] = useState([]);
  const [risks, setRisks] = useState([]);
  const [ai, setAI] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);
  useEffect(() => {
    api.get("/security/findings").then(r => setItems(asArray(r.data)));
    api.get("/security/risks").then(r => setRisks(asArray(r.data)));
  }, []);
  const recommend = async () => {
    setLoadingAI(true);
    try { const r = await api.post("/ai/security-recommendations"); setAI(r.data.recommendations); } finally { setLoadingAI(false); }
  };
  const open = items.filter(i => i.status !== "resolved").length;
  const crit = items.filter(i => i.severity === "critical").length;
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Security Operations</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">SOC Posture</h1>
        </div>
        <button onClick={recommend} data-testid="ai-security-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-2"><Sparkles size={14}/>{loadingAI?"Generating…":"AI Recommendations"}</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Open Findings" value={open} tone="warn" testid="sec-kpi-open"/>
        <KPI label="Critical" value={crit} tone="bad" testid="sec-kpi-critical"/>
        <KPI label="Open Risks" value={risks.filter(r=>r.status==='open').length} tone="warn" testid="sec-kpi-risks"/>
        <KPI label="Resolved" value={items.filter(i=>i.status==='resolved').length} tone="good" testid="sec-kpi-resolved"/>
      </div>

      {ai && <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5"><div className="font-display font-medium mb-2">AI Recommendations</div><pre className="text-xs whitespace-pre-wrap text-zinc-300 leading-relaxed" data-testid="security-ai-output">{ai}</pre></div>}

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-900 font-display font-medium flex items-center gap-2"><ShieldAlert size={14}/> Findings ({items.length})</div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left px-4 py-2">Title</th><th className="text-left px-4 py-2">MITRE</th><th className="text-left px-4 py-2">Severity</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Detected</th></tr></thead>
          <tbody>
            {items.map(f => (
              <tr key={f.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-4 py-2">{f.title}</td>
                <td className="px-4 py-2 font-mono text-xs text-zinc-500">{f.mitre_attack}</td>
                <td className="px-4 py-2"><SeverityBadge level={f.severity}/></td>
                <td className="px-4 py-2"><StatusBadge status={f.status}/></td>
                <td className="px-4 py-2 font-mono text-xs text-zinc-500">{f.created_at?.slice(0,10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-900 font-display font-medium">Risk Register</div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left px-4 py-2">Risk</th><th className="text-left px-4 py-2">Category</th><th className="text-left px-4 py-2">Likelihood</th><th className="text-left px-4 py-2">Impact</th><th className="text-left px-4 py-2">Treatment</th><th className="text-left px-4 py-2">Status</th></tr></thead>
          <tbody>
            {risks.map(r => (
              <tr key={r.id} className="border-b border-zinc-900">
                <td className="px-4 py-2">{r.title}</td>
                <td className="px-4 py-2 text-zinc-400">{r.category}</td>
                <td className="px-4 py-2"><SeverityBadge level={r.likelihood?.toLowerCase()==='high'?'high':r.likelihood?.toLowerCase()==='medium'?'medium':'low'}>{r.likelihood}</SeverityBadge></td>
                <td className="px-4 py-2 text-zinc-300">{r.impact}</td>
                <td className="px-4 py-2 text-zinc-400">{r.treatment}</td>
                <td className="px-4 py-2"><StatusBadge status={r.status}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
